'use client'

import { useState } from 'react'
import { createClient } from '@/lib/supabase/client'
import { getDashboardPath } from '@/lib/actions/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Eye,
  EyeOff,
  GraduationCap,
  Users,
  Calendar,
  BarChart3,
  FileText,
  MessageSquare,
  Shield,
} from 'lucide-react'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const supabase = createClient()
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (authError) {
        setError(authError.message)
        setLoading(false)
        return
      }

      if (data.user) {
        // Wait for auth state to be established, then get role-based dashboard path and redirect
        try {
          // Wait for session to be established using auth state change
          // This is more reliable than a fixed delay
          const sessionEstablished = await new Promise<boolean>((resolve) => {
            const supabase = createClient()
            const timeout = setTimeout(() => {
              // Fallback: if auth state doesn't change within 2 seconds, proceed anyway
              resolve(false)
            }, 2000)

            // Listen for auth state change
            const {
              data: { subscription },
            } = supabase.auth.onAuthStateChange((event) => {
              if (event === 'SIGNED_IN') {
                clearTimeout(timeout)
                subscription.unsubscribe()
                resolve(true)
              }
            })

            // If already signed in, resolve immediately
            if (data.session) {
              clearTimeout(timeout)
              subscription.unsubscribe()
              resolve(true)
            }
          })

          // Small delay to ensure cookies are propagated (only if session wasn't established via listener)
          if (!sessionEstablished) {
            await new Promise(resolve => setTimeout(resolve, 200))
          }

          const dashboardPath = await getDashboardPath()
          if (dashboardPath) {
            // Use window.location for a hard redirect to ensure cookies are sent
            window.location.href = dashboardPath
          } else {
            // If dashboard path is null, user might not be fully set up
            // Try redirecting to home as fallback
            console.warn('Dashboard path is null, redirecting to home')
            window.location.href = '/'
          }
        } catch (redirectError) {
          // If getting dashboard path fails, redirect to home
          console.error('Error getting dashboard path:', redirectError)
          // Use hard redirect as fallback
          window.location.href = '/'
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during login'
      setError(errorMessage)
      setLoading(false)
    }
  }

  const features = [
    {
      icon: GraduationCap,
      title: 'Smart Exam Management',
      description: 'Create, schedule, and auto-grade NEET/JEE-style exams with instant results',
    },
    {
      icon: Users,
      title: 'Batch & Student Management',
      description: 'Organize students into batches, track attendance, and manage enrollments',
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Deep insights into student progress with detailed reports and trends',
    },
    {
      icon: Calendar,
      title: 'Timetable & Scheduling',
      description: 'Automated timetable generation and class scheduling for your institute',
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp Integration',
      description: 'Send automated notifications and updates directly to parents and students',
    },
    {
      icon: FileText,
      title: 'AI-Powered Question Bank',
      description: 'Upload documents and auto-classify questions by topic and difficulty',
    },
  ]

  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-8 lg:p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-white rounded-full" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Coachsync</h1>
              <p className="text-emerald-200 text-sm">Coaching Management Platform</p>
            </div>
          </div>

          {/* Main Headline */}
          <div className="mb-10">
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              Empower Your
              <br />
              Coaching Institute
            </h2>
            <p className="text-emerald-100 text-lg max-w-md">
              The all-in-one platform to manage students, conduct exams, track progress, and grow your educational business.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm">{feature.title}</h3>
                  <p className="text-emerald-200 text-xs mt-0.5 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer - Trust Signals */}
        <div className="relative z-10 mt-8">
          <div className="flex items-center gap-6 text-emerald-200 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Secure & Encrypted</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center bg-zinc-50 dark:bg-zinc-900 px-4 py-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Coachsync</h1>
              <p className="text-zinc-500 dark:text-zinc-400 text-sm">Coaching Management Platform</p>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
              <CardDescription className="text-base">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-medium bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {/* Help Text */}
              <div className="mt-6 text-center">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  Having trouble signing in?{' '}
                  <a href="mailto:support@coachsync.app" className="text-emerald-600 hover:underline">
                    Contact support
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Features - Compact */}
          <div className="lg:hidden mt-8 space-y-3">
            <p className="text-center text-sm text-zinc-500 dark:text-zinc-400 font-medium">
              Why choose Coachsync?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {features.slice(0, 4).map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-zinc-800 shadow-sm"
                >
                  <feature.icon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-xs text-zinc-700 dark:text-zinc-300">{feature.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Copyright */}
          <p className="text-center text-xs text-zinc-400 mt-8">
            &copy; {new Date().getFullYear()} Coachsync. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
